// skipcq: JS-W1028
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

import { db } from '../utils/firebase';
import { onSnapshotWithPermissionRetry } from '../utils/onSnapshotWithPermissionRetry';

function normalizeGroupId(groupId) {
  return typeof groupId === 'string' ? groupId.trim() : '';
}

function readGroupDisplayName(groupSnap) {
  if (!groupSnap.exists()) {
    return '';
  }
  const rawDisplayName = groupSnap.data()?.displayName;
  return typeof rawDisplayName === 'string' ? rawDisplayName.trim() : '';
}

/**
 * Loads breweryGroups.displayName for each group id.
 *
 * @param {string[]} groupIds
 * @returns {Promise<Record<string, string>>}
 */
export async function fetchBreweryGroupDisplayNamesById(groupIds) {
  const uniqueGroupIds = [
    ...new Set(
      (Array.isArray(groupIds) ? groupIds : [])
        .map(normalizeGroupId)
        .filter((groupId) => groupId.length > 0),
    ),
  ];

  if (uniqueGroupIds.length === 0) {
    return {};
  }

  const entries = await Promise.all(
    uniqueGroupIds.map(async (groupId) => {
      try {
        const groupSnap = await getDoc(doc(db, 'breweryGroups', groupId));
        const displayName = readGroupDisplayName(groupSnap);
        return displayName.length > 0 ? [groupId, displayName] : null;
      } catch (fetchError) {
        console.error(`fetchBreweryGroupDisplayNamesById: failed for ${groupId}`, fetchError);
        return null;
      }
    }),
  );

  return Object.fromEntries(entries.filter(Boolean));
}

/**
 * Subscribes to breweryGroups.displayName for each group id.
 *
 * @param {string[]} groupIds
 * @param {(namesById: Record<string, string>, loaded: boolean) => void} onUpdate
 * @returns {() => void}
 */
export function subscribeBreweryGroupDisplayNamesById(groupIds, onUpdate) {
  const uniqueGroupIds = [
    ...new Set(
      (Array.isArray(groupIds) ? groupIds : [])
        .map(normalizeGroupId)
        .filter((groupId) => groupId.length > 0),
    ),
  ];

  if (uniqueGroupIds.length === 0) {
    onUpdate({}, true);
    return () => {
      // No-op unsubscribe.
    };
  }

  const namesById = {};
  const loadedGroupIds = new Set();
  let cancelled = false;

  const unsubscribes = uniqueGroupIds.map((groupId) =>
    onSnapshotWithPermissionRetry(
      onSnapshot,
      doc(db, 'breweryGroups', groupId),
      (groupSnap) => {
        if (cancelled) {
          return;
        }
        const displayName = readGroupDisplayName(groupSnap);
        if (displayName.length > 0) {
          namesById[groupId] = displayName;
        } else {
          delete namesById[groupId];
        }
        loadedGroupIds.add(groupId);
        onUpdate({ ...namesById }, loadedGroupIds.size === uniqueGroupIds.length);
      },
      (snapshotError) => {
        console.error(
          `subscribeBreweryGroupDisplayNamesById: failed for ${groupId}`,
          snapshotError,
        );
        if (cancelled) {
          return;
        }
        loadedGroupIds.add(groupId);
        onUpdate({ ...namesById }, loadedGroupIds.size === uniqueGroupIds.length);
      },
    ),
  );

  return () => {
    cancelled = true;
    unsubscribes.forEach((unsubscribe) => unsubscribe());
  };
}
